import React from 'react';

import { getStartingPosition, Select } from '@aline/common';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { RuleRow } from './RuleRow';
import type { PlaybookDetailRule } from './types';

interface SortableRuleRowProps {
  onDelete: () => void;
  onSelect: () => void;
  rule: PlaybookDetailRule;
  selected: boolean;
  disabled?: boolean;
}

/**
 * A rule row that can be picked up and dropped elsewhere in the list. The
 * sortable id is the rule's own id, which is what the drop handler looks up in
 * the order.
 *
 * Only the listeners go on the grip, not `attributes`: those would make the
 * grip a focusable `role="button"` and announce keyboard pick-up instructions
 * that no keyboard sensor here honours.
 */
export const SortableRuleRow = React.memo(function SortableRuleRow({
  disabled = false,
  onDelete,
  onSelect,
  rule,
  selected,
}: SortableRuleRowProps): JSX.Element {
  const { isDragging, listeners, setNodeRef, transform, transition } = useSortable({
    disabled,
    id: rule.id,
  });

  return (
    <RuleRow
      dataCy={Select.playbooks.ruleRow}
      deleteDisabled={disabled}
      dragListeners={listeners}
      dragging={isDragging}
      /**
       * Untitled rules (e.g. ones created before titles existed) fall back to
       * their starting position so the row still says what the rule is.
       */
      fallbackLabel={getStartingPosition(rule.positions).ruleText || undefined}
      rootRef={setNodeRef}
      selected={selected}
      // Translate only, like the position and category cards: dnd-kit's scale
      // reports a height ratio that is only ever 1 for these uniform rows.
      style={{ transform: CSS.Translate.toString(transform), transition }}
      title={rule.title}
      onDelete={onDelete}
      onSelect={onSelect}
    />
  );
});
